import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
    useFloating,
    useClick,
    useHover,
    useDismiss,
    useInteractions,
    useTransitionStyles,
    FloatingPortal,
    FloatingFocusManager,
    offset,
    flip,
    shift,
    autoUpdate,
} from '@floating-ui/react';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { normalizeFloatingPlacement } from '../../../utils/placementMap';

import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Popover');

const MIDDLEWARE = [offset(8), flip(), shift({ padding: 8 })];
const HOVER_DELAY = { open: 100, close: 200 };

function Popover({
    children,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    content,
    getRef,
    getContentRef,
    placement = 'top',
    trigger = ['click'],
    overlayStyle,
    overlayInnerStyle,
    ...props
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const classes = useClasses(useStyles, classesProp);

    const override = getOverrides(overridesProp, Popover.overrides);

    const { dismissConfig, visible: controlledVisible, onVisibleChange, ...restProps } = props;
    const isControlled = controlledVisible !== undefined;
    const isOpen = isControlled ? controlledVisible : internalOpen;

    // Handle visibility changes
    const handleOpenChange = (newOpen) => {
        if (isControlled) {
            onVisibleChange?.(newOpen);
        } else {
            setInternalOpen(newOpen);
            onVisibleChange?.(newOpen);
        }
    };

    const setIsOpen = handleOpenChange;

    const floatingPlacement = normalizeFloatingPlacement(placement);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: MIDDLEWARE,
        whileElementsMounted: autoUpdate,
        placement: floatingPlacement,
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 200,
        initial: {
            opacity: 0,
        },
        open: {
            opacity: 1,
        },
        close: {
            opacity: 0,
        },
    });

    const enableClick = trigger.includes('click');
    const enableHover = trigger.includes('hover');

    const click = useClick(context, {
        enabled: enableClick,
    });

    const hover = useHover(context, {
        enabled: enableHover,
        delay: HOVER_DELAY,
    });

    const dismiss = useDismiss(context, {
        escapeKey: true,
        outsidePress: true,
        ...dismissConfig,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, dismiss]);

    const rootClassName = useMemo(
        () =>
            classnames(
                classes.root,
                classNameProp,
                override.root?.className,
                context.placement && `hoi-poi-popover-placement-${context.placement}`,
            ),
        [classes.root, classNameProp, override.root?.className, context.placement],
    );

    const contentClassName = useMemo(
        () => classnames(classes.content, 'hoi-poi-popover-inner', override.content?.className),
        [classes.content, override.content?.className],
    );

    return (
        <>
            <span ref={refs.setReference} {...getReferenceProps()} className={classes.trigger}>
                {children}
            </span>

            {isMounted && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
                        <div
                            ref={(node) => {
                                refs.setFloating(node);
                                if (getRef) getRef(node);
                            }}
                            style={{ ...floatingStyles, ...transitionStyles, ...overlayStyle }}
                            className={rootClassName}
                            {...getFloatingProps()}
                            {...restProps}
                        >
                            <div className={contentClassName} style={overlayInnerStyle}>
                                {getContentRef ? (
                                    <div ref={(ref) => getContentRef(ref)}>{content}</div>
                                ) : (
                                    content
                                )}
                            </div>
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}

Popover.overrides = ['root', 'content', 'trigger'];

Popover.propTypes = {
    /** Element that triggers the popover */
    children: PropTypes.node.isRequired,
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Content to be displayed inside the popover (text, HTML, or React component) */
    content: PropTypes.any,
    /** Array of interaction types that will trigger the popover to show */
    trigger: PropTypes.arrayOf(PropTypes.oneOf(['click', 'hover'])),
    /** Controls the position of the popover relative to the trigger element */
    placement: PropTypes.oneOf([
        // rc-tooltip format
        'top',
        'topLeft',
        'topRight',
        'bottom',
        'bottomLeft',
        'bottomRight',
        'left',
        'leftTop',
        'leftBottom',
        'right',
        'rightTop',
        'rightBottom',
        // floating-ui format
        'top-start',
        'top-end',
        'bottom-start',
        'bottom-end',
        'left-start',
        'left-end',
        'right-start',
        'right-end',
    ]),
    /** Function to get a reference to the popover floating element */
    getRef: PropTypes.func,
    /** Function to get a reference to the popover content element */
    getContentRef: PropTypes.func,
    /** Additional styles for the popover overlay container */
    overlayStyle: PropTypes.object,
    /** Additional styles for the inner content wrapper */
    overlayInnerStyle: PropTypes.object,
    /** Controlled visibility state */
    visible: PropTypes.bool,
    /** Callback when visibility changes */
    onVisibleChange: PropTypes.func,
    /** Merged into Floating UI `useDismiss` (e.g. custom `outsidePress` to ignore nested portaled UI). */
    dismissConfig: PropTypes.object,
};

export default React.memo(Popover);
